import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  Grid,
} from "@mui/material";
import { FilterList, Search } from "@mui/icons-material";
import ResetIcon from "../../../assets/image/reseticon.svg";
import { CustomInput } from "../../../components/CustomInput";
import { CustomSearchInput } from "./../../../components/CustomSearchInput";
import { getSegmentCountByAgent } from "../../../services/air/air-booking";
import { errorMsg } from "../../../utils/toast-msg";

const SegmentCountByAgent = () => {
  const [data, setData] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [queryData, setQueryData] = useState({});
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getSegmentCountByAgent();

        setData(data?.payload);
        setSearchData(data?.payload);
      } catch (error) {
        if (error?.response?.status === 403) {
          errorMsg("You have no permission to access this page");
        } else {
          console.error("An error occurred while fetching data:", error);
          errorMsg("An unexpected error occurred. Please try again later.");
        }
      }
    })();
  }, []);

  const handleSearchItems = (e) => {
    const searchInput = e.target.value.toLowerCase();
    if (searchInput) {
      const filteredData = data.filter((item) =>
        Object.values(item).some((val) =>
          String(val).toLowerCase().includes(searchInput)
        )
      );
      setSearchData(filteredData);
    } else {
      setSearchData(data);
    }
  };

  const handleFilterClick = () => {
    setQueryData({});
    setShowFilter((prev) => !prev);
  };

  const handleQueryChange = ({ target }) => {
    const newData = { ...queryData };
    newData[target.name] = target.value;
    setQueryData(newData);
  };

  const handleFilterSubmit = async () => {
    const params = {
      fromDate: queryData.fromDate || null,
      toDate: queryData.toDate || null,
    };

    try {
      const { data } = await getSegmentCountByAgent(params);

      setData(data.payload);
      setSearchData(data.payload);
    } catch (error) {
      console.error("Error fetching filtered sgements count:", error);
    }
  };

  const handleFilterReset = () => {
    setQueryData({});
  };

  return (
    <Box mt={2}>
      <Box
        sx={{
          padding: "16px",
          backgroundColor: "var(--color-bg-white)",
          borderRadius: "12px",
        }}
      >
        {/* Top Bar */}
        <Box
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderRadius: "12px",
          }}
          mb={2}
        >
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              position: "relative",
            }}
          >
            {/* Search Input */}
            <Box
              style={{ position: "relative" }}
              className={"placeholderColor"}
            >
              <Search
                style={{
                  position: "absolute",
                  top: "4px",
                  left: "5px",
                  color: "var(--color-draft)",
                  zIndex: 1,
                }}
              />
              <CustomSearchInput
                onChange={handleSearchItems}
                type="text"
                placeholder="Quick Search"
              />
            </Box>

            {/* Filter Button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "var(--color-bg-hover-sitebar)",
                borderRadius: "4px",
                padding: "5px 5px",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 400,
                  fontSize: "14px",
                  color: "var(--color-draft)",
                  padding: "0px 4px",
                  paddingRight: "10px",
                  cursor: "pointer",
                }}
              >
                Filter
              </Typography>
              <FilterList
                sx={{
                  color: "var(--color-draft)",
                  backgroundColor: "var(--color-bg-white)",
                  padding: "2px 4px",
                  fontSize: "22px",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
                onClick={handleFilterClick}
              />
            </Box>

            {/* Reset Button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "var(--color-bg-hover-sitebar)",
                borderRadius: "4px",
                padding: "5px 5px",
                cursor: "pointer",
              }}
              onClick={handleFilterReset}
            >
              <Typography
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 400,
                  fontSize: "14px",
                  color: "var(--color-draft)",
                  padding: "0px 4px",
                  paddingRight: "10px",
                }}
              >
                Reset
              </Typography>
              <img
                src={ResetIcon}
                alt="Reset"
                style={{
                  width: "16px",
                  height: "16px",
                  backgroundColor: "var(--color-bg-white)",
                  padding: "6px 6px",
                  borderRadius: "5px",
                }}
              />
            </Box>
          </Box>
        </Box>

        {showFilter ? (
          <Box sx={{ transition: "max-height 0.7s ease" }} mb={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2.5}>
                <CustomInput
                  type="date"
                  name="fromDate"
                  placeholder="Select From Date"
                  onChange={handleQueryChange}
                  value={queryData.fromDate || ""}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2.5}>
                <CustomInput
                  type="date"
                  name="toDate"
                  placeholder="Select To Date"
                  onChange={handleQueryChange}
                  value={queryData.toDate || ""}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <button
                  style={{
                    backgroundColor: "var(--color-primary)",
                    color: "var(--color-bg-white)",
                    borderRadius: "5px",
                    padding: "9px 12px",
                    fontFamily: "Outfit",
                    fontSize: "13px",
                    fontWeight: 500,
                    border: "none",
                    cursor: "pointer",
                  }}
                  onClick={handleFilterSubmit}
                >
                  Apply Filter
                </button>
              </Grid>
            </Grid>
          </Box>
        ) : null}

        {/* Table Section */}
        <TableContainer
          sx={{
            backgroundColor: "var(--color-bg-white)",
          }}
        >
          <Table
            sx={{
              minWidth: "900px",
              tableLayout: "auto",
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "var(--color-primary-light)",
                  color: "var(--color-bg-white)",
                  fontWeight: "500",
                  fontSize: "12px",
                }}
              >
                <TableCell
                  sx={{
                    color: "var(--color-bg-white)",
                    textAlign: "center",
                    fontSize: "12px",
                    padding: "4px",
                    fontWeight: 400,
                    fontFamily: "Outfit",
                  }}
                >
                  Agent ID
                </TableCell>
                <TableCell
                  sx={{
                    color: "var(--color-bg-white)",
                    textAlign: "center",
                    fontSize: "12px",
                    padding: "4px",
                    fontWeight: 400,
                    fontFamily: "Outfit",
                  }}
                >
                  Agency Name
                </TableCell>
                <TableCell
                  sx={{
                    color: "var(--color-bg-white)",
                    textAlign: "center",
                    fontSize: "12px",
                    padding: "4px",
                    fontWeight: 400,
                    fontFamily: "Outfit",
                  }}
                >
                  Total Segment (Expect Non Seg)
                </TableCell>
                <TableCell
                  sx={{
                    color: "var(--color-bg-white)",
                    textAlign: "center",
                    fontSize: "12px",
                    padding: "4px",
                    fontWeight: 400,
                    fontFamily: "Outfit",
                  }}
                >
                  Total Segment (Only Non Seg)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(searchData || [])?.map((report) => (
                <React.Fragment key={report.id}>
                  <TableRow
                    sx={{
                      backgroundColor: "var(--color-bg-white)",
                    }}
                  >
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: 500,
                        fontSize: "12px",
                        color: "var(--color-airline-code)",
                        cursor: "pointer",
                        fontFamily: "Outfit",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        padding: "8px",
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "var(--color-bg-hover-sitebar)",
                          border: "1px solid var(--color-draft)",
                          color: "var(--color-airline-code)",
                          padding: "1px 0px",
                          borderRadius: "2px",
                        }}
                      >
                        {report.identificationShortCode}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: 500,
                        fontSize: "12px",
                        color: "var(--color-gray)",
                        fontFamily: "Outfit",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        padding: "8px",
                      }}
                    >
                      {report.companyName}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: 500,
                        fontSize: "12px",
                        color: "var(--color-gray)",
                        fontFamily: "Outfit",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        padding: "8px",
                      }}
                    >
                      {report.totalSegmentExpectNonSeg}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: 500,
                        fontSize: "12px",
                        color: "var(--color-gray)",
                        fontFamily: "Outfit",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        padding: "8px",
                      }}
                    >
                      {report.totalSegmentOnlyNonSeg}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default SegmentCountByAgent;
